import {
  Bell,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Mail,
  Link,
  Gamepad2,
  AlertCircle,
  Zap,
  Users,
  Trash2,
} from 'lucide-react';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { Button } from '@@/buttons';
import { Select } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { ChannelConfigurationFields } from './ChannelConfigurationFields/ChannelConfigurationFields';
import { AlertSettingsFormValues, NotificationChannelType } from './types';
import { useNotificationChannel } from './useNotificationChannel';

const notificationChannelTypes = [
  { value: 'slack', label: 'Slack' },
  { value: 'email', label: 'Email' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'msteamsv2', label: 'Microsoft Teams V2' },
];

interface Props {
  index: number;
  defaultExpanded?: boolean;
}

export function NotificationChannelConfiguration({
  index,
  defaultExpanded,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);
  const { values, setFieldValue } = useFormikContext<AlertSettingsFormValues>();
  const channel = useNotificationChannel(index);

  return (
    <div className="border-gray-200 th-dark:border-gray-700 rounded-lg border bg-[var(--bg-widget-color)] shadow-sm transition-shadow hover:shadow-md">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center space-x-3">
            <button
              type="button"
              onClick={() => setIsExpanded((expanded) => !expanded)}
              className="hover:bg-gray-100 th-dark:hover:bg-gray-700 flex-shrink-0 rounded p-1 transition-colors"
              data-cy={`expand-channel-${channel.id}`}
            >
              {isExpanded ? (
                <ChevronDown className="text-gray-400 th-dark:text-gray-500 h-4 w-4" />
              ) : (
                <ChevronRight className="text-gray-400 th-dark:text-gray-500 h-4 w-4" />
              )}
            </button>

            <div className="flex flex-1 items-center space-x-3">
              <div className="flex-shrink-0">
                {getChannelTypeIcon(channel.type)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center space-x-2">
                  <h4
                    className="truncate text-sm font-medium"
                    title={String(channel.config?.name) || channel.name}
                  >
                    {channel.config?.name || channel.name}
                  </h4>
                  <Badge type="info" className="flex-shrink-0">
                    {notificationChannelTypes.find(
                      (t) => t.value === channel.type
                    )?.label || channel.type}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          <div className="ml-4 flex flex-shrink-0 items-center space-x-3">
            <div className="flex items-center">
              <Switch
                id={`channel-enabled-${channel.id}`}
                name={`channel-enabled-${channel.id}`}
                checked={channel.enabled ?? true}
                onChange={(enabled) =>
                  setFieldValue(
                    `notificationChannels.${index}.enabled`,
                    enabled
                  )
                }
                data-cy={`channel-enabled-${channel.id}`}
              />
            </div>

            <Button
              type="button"
              onClick={() =>
                setFieldValue(
                  'notificationChannels',
                  values.notificationChannels.toSpliced(index, 1)
                )
              }
              color="dangerlight"
              size="small"
              icon={Trash2}
              data-cy={`delete-channel-${channel.id}`}
              className="!p-2"
            />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-gray-100 th-dark:border-gray-700 border-t bg-[var(--bg-body-color)] p-4">
          <div className="space-y-4">
            <div>
              <label
                htmlFor={`channel-type-${channel.id}`}
                className="mb-2 block text-sm font-medium"
              >
                Notification Type <span className="text-red-500">*</span>
              </label>
              <Select
                id={`channel-type-${channel.id}`}
                value={channel.type}
                onChange={(e) =>
                  setFieldValue(`notificationChannels.${index}`, {
                    ...channel,
                    type: e.target.value as NotificationChannelType,
                    name: e.target.value,
                    config: {},
                  })
                }
                options={notificationChannelTypes}
                data-cy={`channel-type-select-${channel.id}`}
                className="w-full"
              />
            </div>

            <div>
              <h5 className="mb-3 text-sm font-medium">Configuration</h5>
              <ChannelConfigurationFields index={index} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getChannelTypeIcon(type: NotificationChannelType) {
  const iconMap: Record<
    NotificationChannelType,
    React.ComponentType<{ className?: string }>
  > = {
    slack: MessageSquare,
    email: Mail,
    webhook: Link,
    discord: Gamepad2,
    pagerduty: AlertCircle,
    opsgenie: Zap,
    msteamsv2: Users,
  };
  const IconComponent = iconMap[type] || Bell;
  return <Icon icon={IconComponent} className="text-blue-500 h-5 w-5" />;
}
